/** Polling cadences and the decisions that go with them.
 *
 *  Pure on purpose. The React hook in usePolling.js is a thin wrapper around these, so
 *  the rules — how often, pause when hidden, refetch on return — are unit-testable
 *  without rendering anything. Same reason portalForPath() lives in api.js rather than
 *  inline in a component: a decision no test can reach is a decision that drifts.
 *
 *  ── The compliance constraint, stated where the code is ──────────────────────────
 *  Polling MUST NOT count as user activity. Automatic logoff after inactivity
 *  (§164.312(a)(2)(iii)) exists to protect an unattended screen, and an unattended
 *  screen that refreshes itself every ten seconds is exactly the case it protects. So:
 *
 *   - nothing here dispatches or simulates the events useIdleLogout() listens for
 *     (mousemove, mousedown, keydown, touchstart, scroll, click);
 *   - nothing here calls /auth/refresh, which is one of only three endpoints that
 *     re-issue a token;
 *   - ordinary API reads do not extend a session server-side, and there is no
 *     response interceptor that refreshes on 401, so a poll cannot renew anything.
 *
 *  The effect is that a logged-in tab left alone still logs itself out on schedule
 *  while polling; the poll simply starts failing with 401 once that happens, which is
 *  the correct outcome. polling.test.js pins all of this, including by reading this
 *  source — because the way this breaks is somebody adding a well-meaning
 *  "keep the session alive while polling".
 */

/** An open conversation. Short, because someone is reading it right now. */
export const THREAD_POLL_MS = 10_000;

/** The inbox list. Longer: it is peripheral vision, not the thing being read. */
export const THREAD_LIST_POLL_MS = 30_000;

/** Events that DO count as user activity. Imported by the test, not used at runtime —
 *  it exists so the test can assert this module mentions none of them in anger. */
export const IDLE_ACTIVITY_EVENTS = Object.freeze([
  "mousemove", "mousedown", "keydown", "touchstart", "scroll", "click",
]);

/** How long until the next poll, or null for "do not schedule one".
 *
 *  Returning null rather than a large number is deliberate: a backgrounded tab should
 *  hold NO timer at all, not a slow one. A phone that has been in a pocket overnight
 *  should not wake up owing the server a few thousand requests.
 */
export function pollDelay({ visible, enabled = true, intervalMs }) {
  if (!enabled || !visible) return null;
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) return null;
  return intervalMs;
}

/** Should we fetch immediately on coming back to the foreground?
 *
 *  Yes, always — that is the whole point of pausing. Whatever is on screen was rendered
 *  before the tab was hidden, so it is stale by at least however long that was, and the
 *  user is looking at it right now. Waiting out a full interval first would show them
 *  known-stale messages for ten more seconds.
 */
export function shouldRefetchOnReturn({ wasHidden, enabled = true }) {
  return Boolean(enabled && wasHidden);
}

/** Whether a poll tick should actually fire a request.
 *
 *  Skips while one is already in flight. Without this, a request slower than the
 *  interval stacks: a 12-second response on a 10-second timer means two in flight, then
 *  three, and the thread view starts rendering whichever reply happens to land last.
 */
export function shouldFetchOnTick({ visible, enabled = true, inFlight }) {
  return Boolean(enabled && visible && !inFlight);
}
